import { Button, Card, Typography, Alert, Space, Progress } from 'antd';
import { SyncOutlined, TagsOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { generatePromForAll, getPromGenerateAllStatus } from 'services/product.service';

const { Title, Text } = Typography;

const PromPage = () => {
  const [result, setResult] = useState<{ started: boolean; total: number } | null>(null);

  const { data: status, refetch: refetchStatus } = useQuery(
    'prom-generate-all-status',
    () => getPromGenerateAllStatus().then((res) => res.data),
    // Polls every 3s while a run is in flight so the progress bar stays live;
    // stops polling once it's done (one extra fetch after `running` flips to catch the final count).
    { refetchInterval: (data) => (data?.running ? 3000 : false) },
  );

  const { mutate, isLoading, isError, error } = useMutation(
    (body?: { keywordsOnly?: boolean; onlyMissing?: boolean }) => generatePromForAll(body).then((res) => res.data),
    { onSuccess: (data) => { setResult(data); refetchStatus(); } },
  );

  const progressPercent = status?.total
    ? Math.round((status.processed / status.total) * 100)
    : 0;

  return (
    <Card style={{ maxWidth: 600, margin: '24px auto' }}>
      <Title level={4}>Prom.ua — генерація карток</Title>

      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <Text type='secondary'>
          Проганяє всі товари з увімкненим прапорцем
          {' '}
          <code>For feed</code>
          {' '}
          через AI: генерує альтернативну назву (RU/UA), яка не дублює текст на
          bibikalka.com.ua, а також зберігає копії фото товару з накладеним
          прозорим водяним знаком (bibikalka.com.ua) — для картки на Prom.ua.
        </Text>
        <Text type='secondary'>
          Процес фоновий і може тривати довго на великому каталозі — товари
          обробляються по одному, щоб не впертися в ліміти AI.
        </Text>

        <Button
          type='primary'
          icon={<SyncOutlined />}
          loading={isLoading}
          disabled={status?.running}
          onClick={() => { setResult(null); mutate(); }}
          size='large'
        >
          Запустити генерацію Prom
        </Button>

        <Text type='secondary'>
          Перегенерувати лише ключові слова (UA/RU) для всіх товарів
          {' '}
          <code>For feed</code>
          {' '}
          — назви, описи та фото не змінюються.
        </Text>
        <Button
          icon={<TagsOutlined />}
          loading={isLoading}
          disabled={status?.running}
          onClick={() => { setResult(null); mutate({ keywordsOnly: true, onlyMissing: false }); }}
        >
          Оновити ключові слова
        </Button>

        {status?.running && (
          <div>
            <Text>
              Обробка триває:
              {' '}
              {status.processed}
              {' / '}
              {status.total}
              {status.failed > 0 ? ` (помилок: ${status.failed})` : ''}
            </Text>
            <Progress percent={progressPercent} status='active' />
          </div>
        )}

        {!status?.running && status?.finishedAt && (
          <Alert
            type='info'
            message='Останній прогін завершено'
            description={`Оброблено ${status.processed} / ${status.total}${status.failed > 0 ? `, помилок: ${status.failed}` : ''}. Завершено: ${new Date(status.finishedAt).toLocaleString()}`}
            showIcon
          />
        )}

        {result?.started === false && (
          <Alert
            type='warning'
            message='Вже виконується'
            description='Попередній прогін генерації ще не завершився — дочекайтесь його закінчення.'
            showIcon
          />
        )}

        {result?.started && (
          <Alert
            type='success'
            message='Запущено!'
            description={`Знайдено товарів (For feed): ${result.total}. Обробка триває у фоні — дані з'являться в товарах по мірі готовності.`}
            showIcon
          />
        )}

        {isError && (
          <Alert
            type='error'
            message='Помилка запуску'
            description={(error as any)?.response?.data?.message || (error as any)?.message || 'Невідома помилка'}
            showIcon
          />
        )}
      </Space>
    </Card>
  );
};

export default PromPage;
